import React, { useState } from "react";
import { Accordion, AccordionItem, Button, Checkbox } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { FaPlay, FaPause } from "react-icons/fa";
import { MdOutlinePhoneCallback, MdOutlinePhoneForwarded } from "react-icons/md";
import { useAppContext } from "../../contexts/AppContext";
import { transformFromUTC, transformFromUTCFromISO } from "../../utils/dateUtils";

const SmartListCallPreview = ({
  calls,
  onOpenCall,
  excludedCallIds = [],
  onExcludeToggle,
  display_tz = "Europe/Paris",
}) => {
  const { t } = useTranslation();
  const {
    setPopupAudioUrl,
    setPopupCallId,
    setPopupIsAudioPlaying,
    popupIsAudioPlaying,
    popupAudioUrl,
  } = useAppContext();

  const [playingCallId, setPlayingCallId] = useState(null);

  const handlePlayAudio = (call) => {
    if (playingCallId === call.id && popupIsAudioPlaying) {
      // Pause current
      setPopupIsAudioPlaying(false);
      setPlayingCallId(null);
    } else {
      // Play new
      setPopupAudioUrl(call.audio_url);
      setPopupCallId(call.id);
      setPopupIsAudioPlaying(true);
      setPlayingCallId(call.id);
    }
  };

  const getGradeColor = (grade) => {
    if (grade === -1) return "bg-gray-100 text-gray-600";
    if (grade >= 7) return "bg-green-100 text-green-700";
    if (grade >= 5) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  const renderCallHeader = (item) => {
    const grade = item?.analysis?.call_overall_grade;
    const isIncoming = item?.incoming;
    const callId = item.id || item.objectID;
    const isExcluded = excludedCallIds.includes(callId);

    return (
      <div className="flex items-center gap-4 w-full pr-4">
        {/* Checkbox for inclusion/exclusion */}
        {onExcludeToggle && (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
              }
            }}
            role="button"
            tabIndex={0}
            className="flex items-center"
          >
            <Checkbox
              isSelected={!isExcluded}
              onValueChange={() => onExcludeToggle(callId)}
              size="sm"
              color="primary"
            />
          </div>
        )}

        {/* Direction icon */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
          {isIncoming ? (
            <MdOutlinePhoneCallback
              size={16}
              className="text-green-600"
            />
          ) : (
            <MdOutlinePhoneForwarded
              size={16}
              className="text-blue-600"
            />
          )}
        </div>

        {/* Operator name */}
        <span className="font-medium text-sm flex-1">{item.operator_name || t("Unknown")}</span>

        {/* Date */}
        <span className="text-gray-500 text-xs">
          {Number.isInteger(item.call_timestamp)
            ? format(transformFromUTC(item.call_timestamp, display_tz), "dd/MM/yyyy HH:mm")
            : format(transformFromUTCFromISO(item.call_timestamp, display_tz), "dd/MM/yyyy HH:mm")}
        </span>

        {/* Grade badge */}
        {grade !== undefined && grade !== null && (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getGradeColor(grade)}`}>
            {grade === -1 ? "N.A" : grade.toFixed(1)}
          </span>
        )}

        {/* View full call button */}
        <div className="">
          <Button
            size="sm"
            variant="flat"
            color="primary"
            onPress={() => onOpenCall && onOpenCall(item)}
          >
            {t("Open")}
          </Button>
        </div>

        {/* Play button */}
        {/* {item.audio_url && (
          <Button
            isIconOnly
            size="sm"
            variant="flat"
            color="primary"
            onPress={() => handlePlayAudio(item)}
            className="min-w-8 w-8 h-8"
          >
            {playingCallId === item.id && popupIsAudioPlaying ? (
              <FaPause size={12} />
            ) : (
              <FaPlay size={12} />
            )}
          </Button>
        )} */}
      </div>
    );
  };

  const renderChapters = (chapters) => {
    if (!chapters || chapters.length === 0) return null;

    return (
      <div className="mt-4">
        <div className="text-sm font-semibold text-primary mb-2">{t("Chapters")}</div>
        {chapters.map((chapter, chapterIdx) => (
          <div
            key={`chapter-${chapterIdx}`}
            className="mb-3"
          >
            {chapter?.title && <div className="font-medium text-sm mb-1">{chapter.title}</div>}
            {chapter?.points?.map((point, pointIdx) => (
              <div
                key={`point-${chapterIdx}-${pointIdx}`}
                className="flex items-start text-xs text-gray-600 ml-2 mb-1"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mr-2 mt-1.5" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderCallDetails = (item) => {
    const summary = item?.analysis?.overview || item?.analysis?.call_summary;
    const chapters = item?.analysis?.chapters;
    const callPurpose = item?.analysis?.call_purpose;

    return (
      <div className="pl-12 pr-4 pb-4">
        {/* Call purpose */}
        {callPurpose && (
          <div className="mb-3">
            <span className="text-xs text-gray-500">{t("Purpose")}: </span>
            <span className="text-xs font-medium">{callPurpose}</span>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="mb-3">
            <div className="text-sm font-semibold text-primary mb-1">{t("Summary")}</div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{summary}</p>
          </div>
        )}

        {/* Chapters */}
        {renderChapters(chapters)}
      </div>
    );
  };

  if (!calls || calls.length === 0) {
    return null;
  }

  return (
    <div className="max-h-[500px] overflow-y-auto border border-[#E5E5E5] rounded-lg">
      <Accordion
        selectionMode="multiple"
        className="px-0"
        itemClasses={{
          base: "py-0 w-full",
          title: "font-normal text-sm",
          trigger: "px-4 py-3 data-[hover=true]:bg-gray-50 border-b border-gray-100",
          content: "pt-0 pb-0",
          indicator: "text-gray-400",
        }}
      >
        {calls.map((item) => (
          <AccordionItem
            key={item.id || item.objectID}
            aria-label={item.operator_name}
            title={renderCallHeader(item)}
          >
            {renderCallDetails(item)}
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export default SmartListCallPreview;
